import React from 'react';
import Icon from './icons';
import { useSch } from './context';
import { dayDiff, localParts } from '../../../schedule/model';

/**
 * The now / next strip. `data` = { ids, tally: [{ show, kind, ids }] sorted by size, rep, nn: { cur, next, startedMs } }.
 * Left: what most screens play now (+ who plays something else); right: when that changes.
 */
export default function NowNextCard({ data, ui }) {
  const { t, f, now, showName, col, screenName, year, tzOf } = useSch();
  const { ids, tally, rep, nn } = data;
  const many = ids.length > 1;
  const top = tally[0];
  const cur = nn.cur;

  let pct = 40;
  if (nn.startedMs != null && nn.next && cur.kind !== 'manual') {
    pct = Math.max(4, Math.min(96, Math.round(((now.ms - nn.startedMs) / (nn.next.atMs - nn.startedMs)) * 100)));
  }
  const tag = cur.kind === 'event' ? <span className="sch6-badge">{t(cur.once ? 'sch6.once_badge' : 'sch6.badge_event')}</span>
    : (cur.kind === 'default' ? <span className="sch6-badge sch6-mute">{t('sch6.badge_manual')}</span>
      : (cur.kind === 'manual' ? <span className="sch6-badge sch6-warn">{t('sch6.badge_hold')}</span> : null));

  const label = many
    ? t('sch6.now_on_many', { count: f.num(top.ids.length), total: f.nScreens(ids.length) })
    : t('sch6.now_on_one', { name: screenName(rep) });

  let until = '';
  let nx = null;
  if (nn.next) {
    const p = localParts(nn.next.atMs, tzOf(rep));
    const when = p.date === now.date ? t('sch6.today') : (dayDiff(now.date, p.date) === 1 ? t('sch6.tomorrow') : f.dtxt(p.date, true, year));
    until = p.date !== now.date
      ? t('sch6.until_when', { time: f.tm(p.min), when: dayDiff(now.date, p.date) === 1 ? t('sch6.tomorrow') : f.dtxt(p.date, true, year) })
      : t('sch6.until', { time: f.tm(p.min) });
    nx = { when, time: f.tm(p.min) };
  }

  return (
    <section className="sch6-card sch6-now" aria-label={t('sch6.now_aria')}>
      <div>
        <div className="sch6-eyebrow">
          <span className="sch6-live" />
          {t('sch6.now_clock', { label, day: f.dayName(now.dow), time: f.tm(now.min) })}
        </div>
        <div className="sch6-showname">
          <span className="sch6-sw" style={{ background: col(cur.show) }} />
          {cur.show ? showName(cur.show) : t('sch6.no_show')}{tag}
        </div>
        <div className="sch6-meta">{until}</div>
        <div className="sch6-bar"><i style={{ width: `${pct}%` }} /></div>
        {ui && cur.kind === 'manual' && (
          <>
            <div className="sch6-held"><Icon name="alert" /><span>{ui.heldText}</span></div>
            <div className="sch6-nowact"><button type="button" className="sch6-btn" onClick={ui.onResume}><Icon name="repeat" />{t('sch6.now_resume')}</button></div>
          </>
        )}
        {ui && (cur.kind === 'rule' || cur.kind === 'event') && (ui.confirmEnd ? (
          <div className="sch6-confirm" role="alert" style={{ marginTop: 14 }}>
            <span>{ui.endText}</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="sch6-btn" onClick={ui.onEndNo}>{t('sch6.cancel')}</button>
              <button type="button" className="sch6-btn sch6-danger" onClick={ui.onEndYes} autoFocus>{t('sch6.end_yes')}</button>
            </span>
          </div>
        ) : (
          <div className="sch6-nowact">
            <button type="button" className="sch6-btn" onClick={ui.onManual}><Icon name="screen" />{t('sch6.now_manual')}</button>
            {ui.canEnd && <button type="button" className="sch6-btn sch6-ghost" onClick={ui.onEnd}><Icon name="x" />{t('sch6.now_end')}</button>}
          </div>
        ))}
        {tally.length > 1 && (
          <div className="sch6-split">
            {tally.slice(1).map((o) => (
              <span key={`${o.show}|${o.kind}`}>
                <i className="sch6-sw" style={{ background: col(o.show), width: 10, height: 10 }} />
                {t('sch6.split_line', { n: f.nScreens(o.ids.length), show: o.show ? showName(o.show) : t('sch6.no_show'), names: f.listNames(o.ids.map(screenName), 3) })}
                {o.kind === 'manual' && o.hold && t('sch6.split_until', { time: f.untilTxt(o.hold.untilLocal, o.hold.untilAt, tzOf(o.ids[0]), now.date, year) })}
              </span>
            ))}
          </div>
        )}
      </div>
      {nn.next && (
        <div className="sch6-nx">
          <div className="sch6-eyebrow">
            <Icon name="clock" />
            {many ? t(top.ids.length > 1 ? 'sch6.next_on_more' : 'sch6.next_on', { name: screenName(rep) }) : t('sch6.next')}
          </div>
          <div className="sch6-showname" style={{ fontSize: 17 }}>
            <span className="sch6-sw" style={{ background: col(nn.next.show) }} />
            {nn.next.show ? showName(nn.next.show) : t('sch6.no_show')}
          </div>
          <div className="sch6-meta">{t('sch6.starts_at', { when: nx.when, time: nx.time })}</div>
        </div>
      )}
    </section>
  );
}
